import React from 'react';
import {
	FormattedMessage,
	defineMessages,
} from 'react-intl';
import {
	useSelector,
} from 'react-redux';
import {
	Link,
} from 'react-router5';

import ConductorLink from '~/components/patterns/links/ConductorLink';
import LogoutMenuItem from '~/components/app/LogoutMenuItem';
import MainNavigation from '~/components/logic/navigations/MainNavigation';
import NavigationIcon, {
	NavigationIconType,
} from '~/components/patterns/icons/NavigationIcon';
import MenuItemLayout from '~/components/atoms/navigations/builders/MenuItemLayout';
import MultiwebsitesNavigation from '~/components/atoms/navigations/websites/MultiwebsitesNavigation';
import ScreenSidebarLayout from '~/components/patterns/layout/ScreenSidebarLayout';
import ShadowLogo from '~/components/patterns/layout/ShadowLogo';
import SidebarProfileInfo from '~/components/app/SidebarProfileInfo';
import SidebarProfileLayout from '~/components/patterns/layout/SidebarProfileLayout';
import SiteLogo from '~/components/app/SiteLogo';
import StatusDot, {
	StatusDotStatus,
} from '~/components/patterns/statuses/StatusDot';
import TeamWebsitesDropdown from '~/components/app/TeamWebsitesDropdown';
import UserNav from '~/components/patterns/navigations/UserNav';
import UserNavigationToggler from '~/components/app/UserNavigationToggler';
import WebsitesNavToggle from '~/components/app/WebsitesNavToggle';

import useAccountId from '~/hooks/useAccountId';
import useAccountIsShadow from '~/hooks/useAccountIsShadow';
import useAccountProblems from '~/hooks/useAccountProblems';
import useEffectiveHomeAccountId from '~/hooks/useEffectiveHomeAccountId';
import useHasWebsitesScreen from '~/hooks/useHasWebsitesScreen';
import useKingdomAdminFeatures from '~/hooks/useKingdomAdminFeatures';
import useOpenSwitchAccountModal from '~/hooks/useOpenSwitchAccountModal';
import useUrlState from '~/hooks/useUrlState';
import useViewportType from '~/hooks/useViewportType';
import useWebsiteAccountName from '~/hooks/useWebsiteAccountName';
import NavigationLayoutToggle from '~/components/app/NavigationLayoutToggle';
import useNavigationLayout from '~/hooks/useNavigationLayout';
import useWebsiteId from '~/hooks/useWebsiteId';
import useMenuExpandedState from '~/hooks/useMenuExpandedState';

import {
	isPhase3,
} from '~/utilities/viewportSize';

import {
	IsConductorClient,
} from '~/config';

import {
	isWebsiteSwitcherOpenSelector,
} from '~/state/ui/menu/selectors';

import {
	useSelectedAccount,
} from '~/contexts/SelectedAccountContext';



const messages = defineMessages({
	account: {
		id: 'ui.menu.account',
	},
	conductor: {
		id: 'ui.menu.conductor',
	},
	profile: {
		id: 'ui.menu.profile',
	},
});



const ScreenSidebarInner: React.FC = () => {
	const accountId = useAccountId();
	const effectiveHomeAccountId = useEffectiveHomeAccountId();
	const hasWebsitesScreen = useHasWebsitesScreen();
	const kingdomAdminFeatures = useKingdomAdminFeatures();
	const openSwitchAccountModal = useOpenSwitchAccountModal(true);
	const urlState = useUrlState();
	const viewportType = useViewportType();
	const websiteAccountName = useWebsiteAccountName(useWebsiteId());
	const [
		currentLayout,
		,
		showToggle,
	] = useNavigationLayout();
	const [
		isMenuExpanded,
		,
		isMenuMinimized,
	] = useMenuExpandedState();

	// Get selectedAccountId from context
	const {
		selectedAccountId,
	} = useSelectedAccount();

	const accountIsShadow = useAccountIsShadow(accountId);
	const accountProblems = useAccountProblems(effectiveHomeAccountId);

	const effectivePhase3 = isPhase3(viewportType, isMenuExpanded, isMenuMinimized);

	const websitesDropdownOpen = useSelector(isWebsiteSwitcherOpenSelector);

	let conductorNavigation: React.ReactNode = null;
	let profile: React.ReactNode = null;
	const footerItems: Array<{
		content: React.ReactNode,
		highlighted?: boolean,
		name: string,
	}> = [];

	// Admin features only shown in Phase 3 (expanded sidebar) - for old navigation
	if (
		effectivePhase3
		&& kingdomAdminFeatures.areVisible
	) {
		footerItems.push({
			content: (
				<MenuItemLayout
					icon={(
						<NavigationIcon type={NavigationIconType.SwitchAccount} />
					)}
					label="Switch Account"
					onClickCallback={openSwitchAccountModal}
				/>
			),
			name: 'switchAccount',
		});

		footerItems.push({
			content: (
				<Link routeName="generalAdmin">
					<MenuItemLayout
						icon={(
							<NavigationIcon type={NavigationIconType.Lock} />
						)}
						label="General Admin"
					/>
				</Link>
			),
			name: 'generalAdmin',
		});
	}

	// Admin features only shown in Phase 3 (expanded sidebar)
	if (
		effectivePhase3
		&& kingdomAdminFeatures.areAlwaysVisible
	) {
		footerItems.push({
			content: (
				<MenuItemLayout
					icon={(
						<NavigationIcon type={NavigationIconType.ToggleAdmin} />
					)}
					label="Toggle Admin"
					onClickCallback={kingdomAdminFeatures.toggle}
				/>
			),
			name: 'toggleAdmin',
		});
	}

	// Account item - always show in Phase 3 for old navigation
	if (effectivePhase3) {
		let statusIcon: React.ReactNode | undefined;
		if (
			accountProblems.hasWebsitesAtPageCapacity
			|| accountProblems.isSubscriptionSuspended
		) {
			statusIcon = (
				<StatusDot status={StatusDotStatus.Critical} />
			);
		} else if (accountProblems.hasPaymentProblem) {
			statusIcon = (
				<StatusDot status={StatusDotStatus.Warning} />
			);
		}

		footerItems.push({
			content: (
				<Link
					routeName="account.websites"
					routeParams={{
						accountId: accountId ?? effectiveHomeAccountId,
					}}
				>
					<MenuItemLayout
						icon={(
							<NavigationIcon type={NavigationIconType.Account} />
						)}
						iconTag={statusIcon}
						label={(
							<FormattedMessage {...messages.account} />
						)}
					/>
				</Link>
			),
			highlighted: urlState.name.startsWith('account.'),
			name: 'account',
		});

		footerItems.push({
			content: (
				<Link routeName="userProfile">
					<MenuItemLayout
						icon={(
							<NavigationIcon type={NavigationIconType.Profile} />
						)}
						label={(
							<FormattedMessage {...messages.profile} />
						)}
					/>
				</Link>
			),
			highlighted: urlState.name === 'userProfile',
			name: 'profile',
		});
	}

	// Always show Conductor link in Phase 2 (56px vertical sidebar)
	if (IsConductorClient) {
		conductorNavigation = (
			<ConductorLink href="https://app.conductor.com">
				<MenuItemLayout
					icon={(
						<NavigationIcon type={NavigationIconType.Conductor} />
					)}
					label={(
						<FormattedMessage {...messages.conductor} />
					)}
				/>
			</ConductorLink>
		);
	}


	// Phase 2: compact profile, Phase 3: full profile layout
	if (effectivePhase3) {
		profile = (
			<SidebarProfileLayout
				logoutLink={(
					<LogoutMenuItem />
				)}
				profileOverview={(
					<Link routeName="userProfile">
						<SidebarProfileInfo />
					</Link>
				)}
			/>
		);
	} else {
		profile = (
			<UserNavigationToggler />
		);
	}

	const isNewNavigation = currentLayout === 'new';

	// For new navigation, don't show the dropdown separately when selectedAccountId is set
	// (it's rendered inline in MainNavigation instead)
	const shouldShowDropdown = !isNewNavigation || !selectedAccountId;

	return (
		<ScreenSidebarLayout
			conductorNavigation={isNewNavigation ? undefined : conductorNavigation}
			header={isNewNavigation ? undefined : (hasWebsitesScreen && (
				<MultiwebsitesNavigation />
			))}
			logo={(
				accountIsShadow ? (
					<ShadowLogo />
				) : (
					<SiteLogo
						accountId={effectiveHomeAccountId}
					/>
				)
			)}
			menuExpanded={effectivePhase3}
			menuMinimized={isMenuMinimized}
			monochrome={accountIsShadow ?? false}
			navigationLayoutToggle={isNewNavigation ? undefined : (showToggle ? (
				<NavigationLayoutToggle />
			) : undefined)}
			newNavigation={isNewNavigation}
			profile={isNewNavigation ? undefined : profile}
			userNavigation={isNewNavigation ? undefined : (footerItems.length > 0 ? (
				<UserNav items={footerItems} />
			) : undefined)}
			websitesDropdownOpen={websitesDropdownOpen}
			websitesNavigation={isNewNavigation ? undefined : (
				<WebsitesNavToggle accountName={websiteAccountName} />
			)}
			websitesNavigationDropdown={shouldShowDropdown ? <TeamWebsitesDropdown /> : undefined}
		>
			<MainNavigation />
		</ScreenSidebarLayout>
	);
};



export default ScreenSidebarInner;
